import {
  AGENT_COMMAND_PAYLOAD_SCHEMAS,
  AGENT_COMMAND_TERMINAL_STATUSES,
  AGENT_NOZZLE_STATUS_LABELS,
  type AgentAdapterHealth,
  type AgentAdapterStatus,
  type AgentAlarmSeverity,
  type AgentCommandStatus,
  type AgentCommandType,
  type AgentNozzleStatus,
  type AgentStatus,
  type AgentUpdateChannel,
  type CreateAgentCommand,
  createAgentCommandSchema,
} from "@station/contracts";
import type { Tone } from "@station/ui";
import type { ParseResult } from "../shifts/record-values.js";

/**
 * Ajan ekranlarının SAF dönüşümleri (Prompt 14 — web).
 *
 * Buradaki her şey birim testlidir ve React'ten bağımsızdır: göreli zaman, bayt biçimi, adaptör sağlığı,
 * saat sapması, kayıt kodu durumu ve komut formu → istek gövdesi. Sayılar Türkiye biçimindedir (AGENTS.md §6);
 * ajan ölçüleri para DEĞİLDİR (bayt, saniye, adet) — bu yüzden `Money` / `Decimal` gerekmez.
 */

const TR_INTEGER = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const TR_ONE_DECIMAL = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 });

/** Eksi işareti U+2212'dir (tire değil): tabular sayılarda hizalanır ve ekran okuyucu "eksi" der. */
const MINUS = "−";

// ── Göreli zaman ─────────────────────────────────────────────────────────────
/**
 * "3 dk önce". CANLI sayaç DEĞİLDİR (liste 30 sn'de bir tazelenir), bu yüzden saniye gösterilmez;
 * bir dakikadan yeni her şey "az önce"dir. Tam an her zaman `title` olarak da yazılır (çağıran tarafta).
 */
export function formatRelativeTime(value: string | null | undefined, now: Date = new Date()): string {
  if (!value) return "—";
  const at = new Date(value).getTime();
  if (Number.isNaN(at)) return "—";
  const seconds = Math.max(0, Math.round((now.getTime() - at) / 1000));
  if (seconds < 60) return "az önce";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  return `${Math.floor(hours / 24)} gün önce`;
}

/** Kayıt kodunun son kullanmasına kalan süre; dolduysa `null` (çağıran "Süresi doldu" yazar). */
export function formatRemaining(expiresAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes} dk ${seconds} sn` : `${seconds} sn`;
}

// ── Bayt ─────────────────────────────────────────────────────────────────────
const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

/** `1288490188` → `1,2 GB`. Bayt sayısı `bigint` DİZGE de olabilir (sözleşme büyük sayıları dizge taşır). */
export function formatBytes(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const bytes = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < BYTE_UNITS.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const text = unit === 0 ? TR_INTEGER.format(size) : TR_ONE_DECIMAL.format(size);
  return `${text} ${BYTE_UNITS[unit]}`;
}

// ── Saat sapması ─────────────────────────────────────────────────────────────
/**
 * Protokol §5: eşik `AGENT_CLOCK_SKEW_ALERT_SECONDS` (varsayılan 120 sn). Sunucu ajanın saatini DÜZELTMEZ,
 * yalnızca görünür kılar — panel de eşiği aşan sapmayı uyarı rozetiyle gösterir.
 */
export const CLOCK_SKEW_ALERT_MS = 120_000;

export function isClockSkewHigh(ms: number | null | undefined): boolean {
  return ms !== null && ms !== undefined && Math.abs(ms) > CLOCK_SKEW_ALERT_MS;
}

/** `1800` → `+1,8 sn`; `-125000` → `−2 dk 5 sn`; `0` → `0 sn`. */
export function formatClockSkew(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  if (ms === 0) return "0 sn";
  const sign = ms < 0 ? MINUS : "+";
  const abs = Math.abs(ms);
  if (abs < 60_000) return `${sign}${TR_ONE_DECIMAL.format(abs / 1000)} sn`;
  const minutes = Math.floor(abs / 60_000);
  const seconds = Math.round((abs % 60_000) / 1000);
  return `${sign}${minutes} dk ${seconds} sn`;
}

// ── Adaptör sağlığı ──────────────────────────────────────────────────────────
export interface AdapterHealthSummary {
  total: number;
  ok: number;
  /** Çalışmayan adaptörler (`DEGRADED` / `DOWN` / `STOPPED`) — adları satırda yazılır. */
  failing: Array<{ id: string; status: AgentAdapterStatus }>;
}

/**
 * Son heartbeat'teki adaptörlerin özeti. Hiç adaptör bildirilmediyse `total = 0` döner: bu "her şey yolunda"
 * DEĞİLDİR (ajan henüz hiç heartbeat atmamış ya da adaptörsüz kurulmuş olabilir) — çağıran ayrı metin yazar.
 */
export function adapterHealthSummary(
  adapters: readonly AgentAdapterHealth[] | null | undefined,
): AdapterHealthSummary {
  const list = adapters ?? [];
  const failing = list
    .filter((adapter) => adapter.status !== "OK")
    .map((adapter) => ({ id: adapter.id, status: adapter.status }));
  return { total: list.length, ok: list.length - failing.length, failing };
}

export function adapterHealthTone(summary: AdapterHealthSummary): Tone {
  if (summary.total === 0) return "neutral";
  if (summary.failing.length === 0) return "success";
  return summary.failing.some((adapter) => adapter.status === "DOWN") ? "danger" : "warning";
}

// ── Kayıt kodu durumu ────────────────────────────────────────────────────────
export type EnrollmentCodeState = "ACTIVE" | "USED" | "REVOKED" | "EXPIRED";

/**
 * Sunucudaki türetmenin (`agents-store.ts > enrollmentCodeState`) BİREBİR aynısı. Panel bunu kendisi hesaplar:
 * açık duran bir sayfada "Bekliyor" rozeti, kod dolduğunda yeniden sorgulamayı beklemeden "Süresi doldu" olur.
 */
export function enrollmentCodeState(
  code: { usedAt: string | null; revokedAt: string | null; expiresAt: string },
  now: Date = new Date(),
): EnrollmentCodeState {
  if (code.usedAt !== null) return "USED";
  if (code.revokedAt !== null) return "REVOKED";
  return new Date(code.expiresAt).getTime() <= now.getTime() ? "EXPIRED" : "ACTIVE";
}

// ── Tonlar (renk TEK BAŞINA anlam taşımaz: her rozette metin de vardır) ──────
export const AGENT_STATUS_TONE: Record<AgentStatus, Tone> = {
  ACTIVE: "success",
  REVOKED: "danger",
};

export const AGENT_COMMAND_STATUS_TONE: Record<AgentCommandStatus, Tone> = {
  PENDING: "warning",
  DELIVERED: "info",
  ACKED: "info",
  SUCCEEDED: "success",
  FAILED: "danger",
  EXPIRED: "neutral",
  CANCELLED: "neutral",
};

export const AGENT_ADAPTER_STATUS_TONE: Record<AgentAdapterStatus, Tone> = {
  OK: "success",
  DEGRADED: "warning",
  DOWN: "danger",
  STOPPED: "neutral",
};

export const AGENT_ALARM_SEVERITY_TONE: Record<AgentAlarmSeverity, Tone> = {
  INFO: "info",
  WARNING: "warning",
  CRITICAL: "danger",
};

export const ENROLLMENT_CODE_STATE_TONE: Record<EnrollmentCodeState, Tone> = {
  ACTIVE: "success",
  USED: "info",
  REVOKED: "neutral",
  EXPIRED: "neutral",
};

const NOZZLE_STATUS_TONE: Record<AgentNozzleStatus, Tone> = {
  IDLE: "neutral",
  CALLING: "info",
  AUTHORIZED: "info",
  FUELLING: "success",
  OFFLINE: "danger",
};

function isNozzleStatus(value: string): value is AgentNozzleStatus {
  return value in AGENT_NOZZLE_STATUS_LABELS;
}

/**
 * Cihaz durumu sözleşmede serbest METİNDİR (`agentDeviceStateSchema.status`): tabanca durumları bilinen
 * kümeden gelir ve Türkçeleşir, tanınmayan değer HAM yazılır — ajan yenilendiğinde panel kırılmaz.
 */
export function deviceStateLabel(status: string): string {
  return isNozzleStatus(status) ? AGENT_NOZZLE_STATUS_LABELS[status] : status;
}

export function deviceStateTone(status: string): Tone {
  return isNozzleStatus(status) ? NOZZLE_STATUS_TONE[status] : "neutral";
}

/** Sonuçlanmış komut bir daha değişmez: iptal düğmesi yalnızca bunun DIŞINDAKİ durumlarda çizilir. */
export function isCommandCancellable(status: AgentCommandStatus): boolean {
  return !(AGENT_COMMAND_TERMINAL_STATUSES as readonly AgentCommandStatus[]).includes(status);
}

// ── Son heartbeat'in ham gövdesinden okunan ölçüler ─────────────────────────
export interface HeartbeatMetrics {
  deadLetterCount: number | null;
  diskFreeBytes: number | null;
  diskTotalBytes: number | null;
  memoryRssBytes: number | null;
  uptimeSeconds: number | null;
  commit: string | null;
}

function numberAt(source: unknown, ...path: string[]): number | null {
  let node: unknown = source;
  for (const key of path) {
    if (typeof node !== "object" || node === null) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === "number" && Number.isFinite(node) ? node : null;
}

/**
 * Ölü mektup sayısı, disk ve bellek ajan AYRINTISINDA ayrı alan DEĞİLDİR; son heartbeat gövdesinde durur
 * (`agentDetailSchema.lastHeartbeat`). Gövde ham `Record<string, unknown>` olduğu için okuma SAVUNMACIDIR:
 * eksik / beklenmedik alan `null` olur ve ekranda tire görünür — ajan sürümü ilerlediğinde panel kırılmaz.
 */
export function heartbeatMetrics(raw: Record<string, unknown> | null | undefined): HeartbeatMetrics {
  const commit = raw?.commit;
  return {
    deadLetterCount: numberAt(raw, "deadLetterCount"),
    diskFreeBytes: numberAt(raw, "disk", "freeBytes"),
    diskTotalBytes: numberAt(raw, "disk", "totalBytes"),
    memoryRssBytes: numberAt(raw, "memory", "rssBytes"),
    uptimeSeconds: numberAt(raw, "uptimeSeconds"),
    commit: typeof commit === "string" && commit !== "" ? commit : null,
  };
}

/** `86400` → `1 gün 0 sa`; ajanın kesintisiz çalışma süresi. */
export function formatUptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) return "—";
  const totalMinutes = Math.floor(seconds / 60);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} gün ${hours} sa`;
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  return `${minutes} dk`;
}

// ── Kurulum komutu ───────────────────────────────────────────────────────────
/** Kayıt kodu penceresinde gösterilen örnek: `agent enroll 7K2M-Q9XA-04TB --api-url https://…`. */
export function agentInstallCommand(code: string, apiUrl: string): string {
  return `agent enroll ${code} --api-url ${apiUrl}`;
}

// ── Komut formu ──────────────────────────────────────────────────────────────
/** `UPDATE_CONFIG` ile değiştirilebilen SAYI alanları (protokol §2 `config` alt kümesi). */
export const AGENT_CONFIG_NUMBER_FIELDS = [
  "heartbeatIntervalSeconds",
  "syncIntervalSeconds",
  "syncBatchSize",
  "totalizerReadingIntervalSeconds",
  "tankReadingIntervalSeconds",
] as const;
export type AgentConfigNumberField = (typeof AGENT_CONFIG_NUMBER_FIELDS)[number];

export interface CommandFormValues {
  type: AgentCommandType;
  /** `RESTART_ADAPTER` — son heartbeat'teki adaptörlerden seçilir. */
  adapterId: string;
  /** `UPLOAD_LOGS` — son N dakika. */
  sinceMinutes: string;
  /** `UPDATE_CONFIG` — boş bırakılan alan GÖNDERİLMEZ (ajanda olduğu gibi kalır). */
  config: Record<AgentConfigNumberField, string>;
  updateChannel: AgentUpdateChannel | "";
  /** Komutun yaşam süresi (dakika); gövdeye saniye olarak çıkar. */
  ttlMinutes: string;
}

export function emptyCommandForm(adapterId = ""): CommandFormValues {
  return {
    type: "UPDATE_PRICES",
    adapterId,
    sinceMinutes: "60",
    config: {
      heartbeatIntervalSeconds: "",
      syncIntervalSeconds: "",
      syncBatchSize: "",
      totalizerReadingIntervalSeconds: "",
      tankReadingIntervalSeconds: "",
    },
    updateChannel: "",
    ttlMinutes: "15",
  };
}

/** Tam sayı alanı: geçersiz metin gövdeye DİZGE olarak çıkar ve Zod hatası o alanın altında görünür. */
function intOrText(text: string): number | string | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

/**
 * Seçili türün yükü. **Ekranda GÖRÜNMEYEN alanlar gövdeye ÇIKMAZ**: form tek durum nesnesi tuttuğu için,
 * "Günlükleri yükle"yi seçip vazgeçen kullanıcının yazdığı dakika, "Adaptörü yeniden başlat" gövdesine
 * sızmamalıdır (Prompt 13 dersi: görünmeyen alanın doğrulama hatası kullanıcıya görünmez).
 * `UPDATE_PRICES` yükünü SUNUCU doldurur — istemci fiyat GÖNDERMEZ.
 */
export function configOverrideOf(values: CommandFormValues): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const field of AGENT_CONFIG_NUMBER_FIELDS) {
    const parsed = intOrText(values.config[field]);
    if (parsed !== undefined) config[field] = parsed;
  }
  if (values.updateChannel !== "") config.updateChannel = values.updateChannel;
  return config;
}

export function commandPayload(values: CommandFormValues): Record<string, unknown> {
  switch (values.type) {
    case "RESTART_ADAPTER":
      return { adapterId: values.adapterId.trim() };
    case "UPLOAD_LOGS":
      return { sinceMinutes: intOrText(values.sinceMinutes) ?? "" };
    case "UPDATE_CONFIG":
      return { config: configOverrideOf(values) };
    default:
      return {};
  }
}

export function commandBody(values: CommandFormValues): unknown {
  const ttl = intOrText(values.ttlMinutes);
  return {
    type: values.type,
    payload: commandPayload(values),
    ttlSeconds: typeof ttl === "number" ? ttl * 60 : (ttl ?? ""),
  };
}

function issuesOf(
  issues: readonly { path: PropertyKey[]; message: string }[],
): Array<{ path: string; message: string }> {
  return issues.map((issue) => ({ path: issue.path.map(String).join("."), message: issue.message }));
}

/**
 * Form → istek gövdesi + doğrulama. Zarf `createAgentCommandSchema` ile, yük TÜR BAŞINA şemayla denetlenir;
 * `UPDATE_PRICES` yükü sunucuda dolduğu için istemcide doğrulanmaz (boş yük geçerlidir).
 */
export function parseCommand(values: CommandFormValues): ParseResult<CreateAgentCommand> {
  const result = createAgentCommandSchema.safeParse(commandBody(values));
  if (!result.success) return { ok: false, issues: issuesOf(result.error.issues) };
  if (values.type === "UPDATE_PRICES") return { ok: true, body: result.data };
  if (values.type === "UPDATE_CONFIG" && Object.keys(configOverrideOf(values)).length === 0)
    return { ok: false, issues: [{ path: "config", message: "En az bir ayar alanı doldurulmalıdır." }] };
  const payload = AGENT_COMMAND_PAYLOAD_SCHEMAS[values.type].safeParse(result.data.payload);
  if (!payload.success) return { ok: false, issues: issuesOf(payload.error.issues) };
  return { ok: true, body: result.data };
}
